/** Entrada movil con etiqueta superior fija por defecto y flotante opcional. */
import { clsx } from 'clsx';
import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { MobileIcon } from './MobileIcon';

type MobileInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
  help?: string;
  icon?: ReactNode;
  iconRight?: ReactNode;
  floatingLabel?: boolean;
  staticLabel?: boolean;
  isRequired?: boolean;
};

export const MobileInput = forwardRef<HTMLInputElement, MobileInputProps>(
  (
    {
      label,
      error,
      help,
      icon,
      iconRight,
      floatingLabel = false,
      staticLabel = false,
      isRequired = false,
      className,
      id,
      placeholder,
      value,
      defaultValue,
      required,
      ...props
    },
    ref,
  ) => {
    const inputId = id || `input-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const [focused, setFocused] = useState(false);
    const hasValue = Boolean(value || defaultValue);
    const useStatic = staticLabel || !floatingLabel;
    const showRequired = isRequired || required;
    const errorId = `${inputId}-error`;
    const helpId = `${inputId}-help`;

    if (useStatic) {
      return (
        <div className="w-full">
          <label
            htmlFor={inputId}
            className={clsx(
              'mb-1.5 block break-words text-sm font-medium',
              error ? 'text-error' : 'text-text-secondary',
            )}
          >
            {label}
            {showRequired && <span aria-hidden="true" className="text-error"> *</span>}
          </label>
          <div
            className={clsx(
              'flex min-h-[52px] items-center gap-2 px-3',
              'rounded-xl border-[1.5px] transition-all duration-200',
              error
                ? 'border-error bg-error-light'
                : focused
                  ? 'border-primary bg-white shadow-sm'
                  : 'border-border bg-white hover:border-border-dark',
            )}
          >
            {icon && (
              <span
                aria-hidden="true"
                className={clsx(
                  'flex h-6 w-6 shrink-0 items-center justify-center',
                  error ? 'text-error' : focused ? 'text-primary' : 'text-text-tertiary',
                )}
              >
                {icon}
              </span>
            )}
            <input
              ref={ref}
              id={inputId}
              value={value}
              defaultValue={defaultValue}
              required={required}
              onFocus={(e) => {
                setFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                props.onBlur?.(e);
              }}
              placeholder={placeholder}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : help ? helpId : undefined}
              className={clsx(
                'h-12 min-h-[44px] w-full min-w-0 flex-1 bg-transparent p-0',
                'truncate text-text-primary placeholder:truncate placeholder:text-text-tertiary',
                'focus:outline-none',
                className,
              )}
              {...props}
            />
            {iconRight && (
              <span className="flex h-11 w-11 shrink-0 items-center justify-center text-text-tertiary">
                {iconRight}
              </span>
            )}
          </div>
          {error && (
            <p id={errorId} className="mt-1.5 flex items-center gap-1 text-sm text-error">
              <MobileIcon name="error" className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 break-words">{error}</span>
            </p>
          )}
          {help && !error && (
            <p id={helpId} className="mt-1.5 break-words text-sm text-text-tertiary">{help}</p>
          )}
        </div>
      );
    }

    return (
      <div className="w-full">
        <div
          className={clsx(
            'relative flex items-center',
            'rounded-xl border-[1.5px] transition-all duration-200',
            error
              ? 'border-error bg-error-light'
              : focused
                ? 'border-primary bg-white shadow-sm'
                : 'border-border bg-white hover:border-border-dark',
          )}
        >
          {icon && (
            <span
              aria-hidden="true"
              className={clsx(
                'absolute left-3 flex h-6 w-6 shrink-0 items-center justify-center pointer-events-none',
                error ? 'text-error' : focused ? 'text-primary' : 'text-text-tertiary',
              )}
            >
              {icon}
            </span>
          )}
          <div className="relative min-w-0 flex-1">
            <label
              htmlFor={inputId}
              className={clsx(
                'absolute pointer-events-none transition-all duration-200 truncate',
                icon ? 'left-11' : 'left-4',
                'max-w-[calc(100%-3rem)]',
                focused || hasValue
                  ? 'top-1.5 text-xs font-medium'
                  : 'top-1/2 -translate-y-1/2 text-base font-normal',
                error
                  ? 'text-error'
                  : focused
                    ? 'text-primary'
                    : 'text-text-tertiary',
              )}
            >
              {label}
              {showRequired && ' *'}
            </label>
            <input
              ref={ref}
              id={inputId}
              value={value}
              defaultValue={defaultValue}
              required={required}
              onFocus={(e) => {
                setFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                props.onBlur?.(e);
              }}
              placeholder={floatingLabel ? (focused ? placeholder : undefined) : placeholder}
              aria-invalid={error ? true : undefined}
              className={clsx(
                'w-full min-w-0 bg-transparent',
                'truncate text-text-primary placeholder:text-text-tertiary',
                'focus:outline-none',
                floatingLabel ? 'pt-6 pb-2' : 'py-4',
                icon ? 'pl-11' : 'pl-4',
                iconRight ? 'pr-12' : 'pr-4',
                className,
              )}
              {...props}
            />
          </div>
          {iconRight && (
            <span className="absolute right-1 flex h-11 w-11 shrink-0 items-center justify-center text-text-tertiary">
              {iconRight}
            </span>
          )}
        </div>
        {error && (
          <p className="mt-1.5 flex items-center gap-1 text-sm text-error">
            <MobileIcon name="error" className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 break-words">{error}</span>
          </p>
        )}
        {help && !error && (
          <p className="mt-1.5 break-words text-sm text-text-tertiary">{help}</p>
        )}
      </div>
    );
  },
);

MobileInput.displayName = 'MobileInput';

/** Area de texto movil con etiqueta estatica (sin encimados). */
type MobileTextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string;
  help?: string;
};

export const MobileTextarea = forwardRef<HTMLTextAreaElement, MobileTextareaProps>(
  ({ label, error, help, className, id, ...props }, ref) => {
    const inputId = id || `textarea-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const [focused, setFocused] = useState(false);
    const errorId = `${inputId}-error`;
    const helpId = `${inputId}-help`;

    return (
      <div className="w-full">
        <label
          htmlFor={inputId}
          className={clsx(
            'mb-1.5 block break-words text-sm font-medium',
            error ? 'text-error' : focused ? 'text-primary' : 'text-text-secondary',
          )}
        >
          {label}
        </label>
        <textarea
          ref={ref}
          id={inputId}
          onFocus={(e) => {
            setFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            props.onBlur?.(e);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : help ? helpId : undefined}
          className={clsx(
            'w-full rounded-xl border-2 bg-white px-4 py-3 text-base',
            'text-text-primary placeholder:text-text-tertiary',
            'transition-all duration-200 resize-none',
            'focus:outline-none focus:shadow-sm',
            error
              ? 'border-error bg-error-light focus:border-error'
              : focused
                ? 'border-primary focus:border-primary'
                : 'border-border hover:border-border-dark',
            className,
          )}
          {...props}
        />
        {error && (
          <p id={errorId} className="mt-1.5 flex items-center gap-1 text-sm text-error">
            <MobileIcon name="error" className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 break-words">{error}</span>
          </p>
        )}
        {help && !error && (
          <p id={helpId} className="mt-1.5 break-words text-sm text-text-tertiary">{help}</p>
        )}
      </div>
    );
  },
);

MobileTextarea.displayName = 'MobileTextarea';
